import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { ProductScreenshot } from 'components/ProductScreenshot';
export const EnableSessionReplayDark = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-dark-mode.png";
export const EnableSessionReplayLight = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-light-mode.png";
export const _frontmatter = {};
const layoutProps = {
  EnableSessionReplayDark,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`PostHog can capture network requests that occur during the browser session, so you can see if your application is sending the expected requests and response, and check the effect of slow network requests or errors on the user experience.`}</p>
    <p>{`You can enable network recording from your `}<a parentName="p" {...{
        "href": "https://app.posthog.com/project/settings"
      }}>{`project settings`}</a>{`:`}</p>
    <ProductScreenshot imageLight={EnableSessionReplayLight} imageDark={EnableSessionReplayDark} alt="Enable network recording in your PostHog" classes="rounded" mdxType="ProductScreenshot" />
    <p>{`When enabled PostHog always captures:`}</p>
    <ul>
      <li parentName="ul">{`the network request URL, `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/API/PerformanceEntry"
        }}>{`performance information`}</a>{` about the request,`}</li>
    </ul>
    <p>{`When request capture is enabled you can also enabled payload and body capture, this includes:`}</p>
    <ul>
      <li parentName="ul">{`the request method`}</li>
      <li parentName="ul">{`the response code`}</li>
      <li parentName="ul">{`request & response headers (if enabled)`}</li>
      <li parentName="ul">{`request & response body (if enabled)`}</li>
    </ul>
    <h2 {...{
      "id": "sensitive-information"
    }}>{`Sensitive information`}</h2>
    <p>{`We automatically scrub some sensitive information from network headers and payloads, but if your request or response payloads could contain sensitive data, you should provide a function to mask the captured data when you initialize PostHog.`}</p>
    <p>{`We have a deny-list of headers that we will never capture (even if you provide a masking function).`}</p>
    <ul>
      <li parentName="ul">{`authorization`}</li>
      <li parentName="ul">{`x-forwarded-for`}</li>
      <li parentName="ul">{`cookie`}</li>
      <li parentName="ul">{`set-cookie`}</li>
      <li parentName="ul">{`x-api-key`}</li>
      <li parentName="ul">{`x-real-ip`}</li>
      <li parentName="ul">{`remote-addr`}</li>
      <li parentName="ul">{`forwarded`}</li>
      <li parentName="ul">{`proxy-authorization`}</li>
      <li parentName="ul">{`x-csrf-token`}</li>
      <li parentName="ul">{`x-csrftoken`}</li>
      <li parentName="ul">{`x-xsrf-token`}</li>
    </ul>
    <p>{`And we redact bodies if we believe they contain`}</p>
    <ul>
      <li parentName="ul">{`credit card numbers`}</li>
      <li parentName="ul">{`social security numbers`}</li>
      <li parentName="ul">{`password`}</li>
      <li parentName="ul">{`secret`}</li>
      <li parentName="ul">{`passwd`}</li>
      <li parentName="ul">{`api_key`}</li>
      <li parentName="ul">{`apikey`}</li>
      <li parentName="ul">{`auth`}</li>
      <li parentName="ul">{`credentials`}</li>
      <li parentName="ul">{`mysql_pwd`}</li>
      <li parentName="ul">{`privatekey`}</li>
      <li parentName="ul">{`private_key`}</li>
      <li parentName="ul">{`token`}</li>
    </ul>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` If you provide a masking function to alter redaction of payloads it entirely replaces PostHog's automatic payload redaction.`}</p>
    </blockquote>
    <h2 {...{
      "id": "how-to-register-a-callback-to-inspect-and-redact-each-network-request"
    }}>{`How to register a callback to inspect and redact each network request`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-js-web"
      }}>{`posthog.init('<ph_project_api_key>', {
    session_recording: {
        maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
            // For example: ignoring a request entirely
            if (request.name.includes('example.com')) {
                return null
            }

            // ... or remove the query string from the URL
            request.name = request.name.split('?')[0]

            // ...redact the request or response body **however makes sense for your payloads**
            request.requestBody = request.requestBody?.replace(/"password":\\s*"[^"]*"/g, '"password": "redacted-password"')
            request.responseBody = request.responseBody?.replace(/"password":\\s*"[^"]*"/g, '"password": "redacted-password"')

            // ... or remove the request body
            request.requestBody = undefined

            // ... and more

            // finally return the request
            return request
        }
    }
})
`}</code></pre>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <h3 {...{
      "id": "recording-from-localhost"
    }}>{`Recording from localhost`}</h3>
    <p>{`Due to the very high volume of network requests that some tools can make (for example when running hot-reload during development) PostHog does not capture network requests when running on localhost`}</p>
    <h3 {...{
      "id": "requests-early-in-the-page-lifecycle-dont-capture-all-information"
    }}>{`Requests early in the page lifecycle don't capture all information`}</h3>
    <p>{`PostHog has to wrap `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` and `}<inlineCode parentName="p">{`xhr`}</inlineCode>{` in order to capture network requests. If your application makes network requests before PostHog has had a chance to wrap them, then PostHog will not capture all information about the request.`}</p>
    <h3 {...{
      "id": "posthog-truncated-the-request-or-response-body"
    }}>{`PostHog truncated the request or response body`}</h3>
    <p>{`In order to maintain service levels we truncate all request and response bodies at 1MB`}</p>
    <h3 {...{
      "id": "a-network-request-says-it-was-redacted"
    }}>{`A network request says it was redacted`}</h3>
    <p>{`We are cautious in what we capture. If you think we're being too cautious you can override the masking function.`}</p>
    <h3 {...{
      "id": "i-want-to-query-the-network-performance-data-i-capture"
    }}>{`I want to query the network performance data I capture`}</h3>
    <p>{`We'd love that too! It's not possible right now but watch this space. You can `}<a parentName="p" {...{
        "href": "https://github.com/PostHog/posthog/issues/19686"
      }}>{`subscribe for updates in GitHub`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      